import { Modal } from "antd";
import { FC, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useLoc } from "@app/shared/lib/hooks";

import { sanitizeCssValue } from "@app/widgets/shared/btnHoverCss";

import { useUIAppState } from "../../UIAppStateProvider";
import { StudentProfileForm } from "../StudentProfileForm/StudentProfileForm";

const SCOPE_CLASS = "ez-cabinet-profile-modal";

export const StudentProfileModal: FC = () => {
  const loc = useLoc();

  const UIAppState = useUIAppState();
  const profileCfg = UIAppState.cabinetProfileSettings;

  const { notifications } = useParams<{ notifications?: string }>();

  useEffect(() => {
    if (!notifications) return;
    UIAppState.setIsProfileDrawerVisible(true, "notifications");
  }, [UIAppState, notifications]);

  // Every interpolated value goes through sanitizeCssValue — crafted tokens
  // like `red; } body { ... }` must not escape their rule.
  const overrideCss = useMemo(() => {
    if (!profileCfg) return "";
    const rules: string[] = [];
    const root = `.${SCOPE_CLASS}`;
    const selBody = `${root} .ant-modal-body`;
    const s = (v: string | undefined) => (v ? sanitizeCssValue(v) : "");
    const bgColor = s(profileCfg.bgColor);
    const fontFamily = s(profileCfg.fontFamily);
    const nameColor = s(profileCfg.nameColor);
    const nameFontSize = s(profileCfg.nameFontSize);
    const emailColor = s(profileCfg.emailColor);
    const emailFontSize = s(profileCfg.emailFontSize);
    const btnBgColor = s(profileCfg.btnBgColor);
    const btnTextColor = s(profileCfg.btnTextColor);
    const btnBorderRadius = s(profileCfg.btnBorderRadius);
    if (bgColor)
      rules.push(`${root} .ant-modal-content,${root} .ant-modal-header{background-color:${bgColor} !important;}`);
    if (fontFamily)
      rules.push(`${root} .ant-modal-content,${root} .ant-modal-body *,${root} .ant-modal-title{font-family:${fontFamily} !important;}`);
    if (nameColor) {
      rules.push(`${root} .ant-modal-title,
        ${selBody} .ant-tabs-tab,
        ${selBody} .ant-tabs-tab-btn,
        ${selBody} h1,${selBody} h2,${selBody} h3,${selBody} h4,${selBody} h5,
        ${selBody} .ant-typography{color:${nameColor} !important;}`);
    }
    if (nameFontSize) {
      rules.push(`${root} .ant-modal-title,
        ${selBody} .ant-tabs-tab,
        ${selBody} .ant-tabs-tab-btn{font-size:${nameFontSize} !important;}`);
    }
    if (emailColor) {
      rules.push(`${selBody},
        ${selBody} .ant-form-item-label label,
        ${selBody} .ant-form-item-extra,
        ${selBody} .ant-typography-secondary,
        ${selBody} .ant-tabs-content,
        ${selBody} .ant-tabs-content *,
        ${selBody} .ant-input,
        ${selBody} .ant-select-selection-item{color:${emailColor} !important;}`);
    }
    if (emailFontSize) {
      rules.push(`${selBody},
        ${selBody} .ant-form-item-label label,
        ${selBody} .ant-form-item-extra,
        ${selBody} .ant-typography-secondary{font-size:${emailFontSize} !important;}`);
    }
    if (btnBgColor)
      rules.push(`${selBody} .ant-btn-primary{background-color:${btnBgColor} !important;border-color:${btnBgColor} !important;}`);
    if (btnTextColor)
      rules.push(`${selBody} .ant-btn-primary,${selBody} .ant-btn-primary span,${selBody} .ant-btn-primary .anticon{color:${btnTextColor} !important;}`);
    if (btnBorderRadius)
      rules.push(`${selBody} .ant-btn-primary{border-radius:${btnBorderRadius} !important;}`);
    return rules.join("\n");
  }, [profileCfg]);

  return (
    <Modal
      open={UIAppState.isProfileDrawerVisible}
      title={loc.word("User profile")}
      width={600}
      destroyOnClose
      onCancel={() => UIAppState.setIsProfileDrawerVisible(false)}
      footer={null}
      className="p-0"
      rootClassName={profileCfg ? SCOPE_CLASS : undefined}
    >
      {overrideCss && <style>{overrideCss}</style>}
      <StudentProfileForm
        viewMode="default"
        defaultTab={UIAppState.defaultProfileTab}
        onSave={() => UIAppState.setIsProfileDrawerVisible(false)}
      />
    </Modal>
  );
};
